import { ActionIcon, Box, Grid, Group, Paper, Skeleton, Stepper, Text } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import classes from './bookingflow-header.module.css'
import { router, usePage } from "@inertiajs/react";
import { queryGetAd, queryGetCampaign } from "@/Services/studio-query-v1";
import { getBlockingStep } from "@/Utilities/booking-flow-helpers";
import { useTranslation } from "react-i18next";

// The booking steps' look (done: green check, current: blue, next: grey), shared by the
// Ad Builder booking flow and the "Let Us Design It" briefing.
export const BookingStepper = ({ active, onStepClick, children }) => (
    <Stepper
        iconSize={24}
        color="green.6"
        classNames={classes}
        completedIcon={
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3.33398 9.3335C3.33398 9.3335 4.66732 9.66683 5.66732 11.6668C5.66732 11.6668 9.37318 5.55572 12.6673 4.3335" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        }
        active={active}
        onStepClick={onStepClick}>
        {children}
    </Stepper>
);

// The booking bar under the studio header: the steps in the middle, the step's actions
// left and right. Also the bar of the briefing's booking steps (AD-225).
// AD-230: on a phone the stepper becomes "2 of 4 Preview" and the actions become icons.
export const BookingFlowBar = ({ leftSection, stepper, rightSection, phoneStep = null, onPhoneBack = null, hiddenOnPhone = false }) => {

    const { t } = useTranslation();

    return (
        <Paper withBorder radius="md" p="md" pos="sticky" top={64} style={{ zIndex: 12 }} className={classes.bar} visibleFrom={hiddenOnPhone ? 'sm' : undefined}>
            <Grid align="center">
                <Grid.Col span={{ base: 'content', sm: 3 }}>
                    <Group justify="flex-start" width="100%" visibleFrom={onPhoneBack ? 'sm' : undefined}>
                        {leftSection}
                    </Group>
                    {onPhoneBack && (
                        <ActionIcon variant="default" size={36} radius={8} hiddenFrom="sm" onClick={onPhoneBack} aria-label={t('common.back')}>
                            <IconChevronLeft size={16} />
                        </ActionIcon>
                    )}
                </Grid.Col>
                <Grid.Col span={{ base: 'auto', sm: 6 }}>
                    <Group justify="center" width="100%" visibleFrom={phoneStep ? 'sm' : undefined}>
                        {stepper}
                    </Group>
                    {phoneStep && (
                        <Text hiddenFrom="sm" ta="center" fz={14} lh={1.55} className={classes.stepCount}>
                            <Text span inherit fw={600}>{t('booking.stepOf', { current: phoneStep.current, total: phoneStep.total })}</Text>
                            {'  '}{phoneStep.name}
                        </Text>
                    )}
                </Grid.Col>
                <Grid.Col span={{ base: 'content', sm: 3 }}>
                    <Group justify="flex-end" width="100%" className={classes.actions}>
                        {rightSection}
                    </Group>
                </Grid.Col>
            </Grid>
        </Paper>
    );
};

export const BookingFlowHeader = ({ leftSection, rightSection }) => {

    const { t } = useTranslation();

    const queryCampaign = queryGetCampaign(route().params.ad);
    const campaign = queryCampaign?.data?.data;
    const queryAd = queryGetAd(route().params.ad);
    // AD-222: a designer on a "Let Us Design It" ad works on the creative only.
    // AD-259: one who finishes it with "Approve / Publish" has no Preview step either.
    const { designerMode = false, designAction = null } = usePage().props;

    const steps = [
        {
            step: 'initial', metadata: { name: t('ads.editPageTitle') }, onClick: () => {
                router.visit(route('studio.ad_builder.edit', { ad: route().params.ad, step: 'initial' }));
            }
        },
        {
            step: 'landingpage', hide: queryAd?.data?.ad?.landing_page != 1, metadata: { name: t('booking.steps.landingpage') }, onClick: () => {
                router.visit(route('studio.ad_builder.edit', { ad: route().params.ad, step: 'landingpage' }));
            }
        },
        {
            step: 'preview', hide: Boolean(designAction) && route().params.step !== 'preview', metadata: { name: t('common.preview') }, onClick: () => {
                router.visit(route('studio.ad_builder.edit', { ad: route().params.ad, step: 'preview' }));
            }
        },
        {
            step: 'delivery', hide: designerMode, metadata: { name: t('booking.steps.delivery') }, onClick: () => {
                router.visit(route('studio.ad_builder.edit', { ad: route().params.ad, step: 'delivery' }));
            }
        },
        {
            step: 'delivery-tag', hide: true, metadata: { name: t('booking.steps.delivery') }, onClick: () => {
                router.visit(route('studio.ad_builder.edit', { ad: route().params.ad, step: 'delivery-tag' }));
            }
        },
        {
            step: 'targeting', hide: designerMode || route().params.step == 'delivery-tag', locked: Boolean(getBlockingStep('targeting', campaign)), metadata: { name: t('booking.steps.targeting') }, onClick: () => {
                router.visit(route('studio.ad_builder.edit', { ad: route().params.ad, step: 'targeting' }));
            }
        },
        {
            step: 'complete', hide: true, metadata: { name: t('booking.steps.complete') }, onClick: () => {
                router.visit(route('studio.ad_builder.edit', { ad: route().params.ad, step: 'complete' }));
            }
        },
    ];

    const visibleSteps = steps.filter(step => !step.hide);
    const stepIndex = visibleSteps.findIndex(step => step.step === (route().params.step || 'initial'));
    // A step the stepper does not offer -- delivery-tag, complete -- sits past the last one.
    const activeStep = stepIndex >= 0 ? stepIndex : visibleSteps.length;

    const currentStep = visibleSteps[Math.min(activeStep, visibleSteps.length - 1)];
    const previousStep = visibleSteps.slice(0, activeStep).reverse().find((step) => !step.locked);

    const stepClickHandler = (step) => {
        const availableSteps = steps.filter(s => !s.hide);
        const stepData = availableSteps[step];
        if (!stepData || stepData.locked) return;
        stepData?.onClick();
    }

    return (
        <BookingFlowBar
            leftSection={leftSection}
            rightSection={rightSection}
            phoneStep={currentStep && { current: Math.min(activeStep + 1, visibleSteps.length), total: visibleSteps.length, name: currentStep.metadata.name }}
            onPhoneBack={previousStep?.onClick}
            hiddenOnPhone={route().params.step === 'complete'}
            stepper={!(queryCampaign.isLoading || queryAd.isLoading) ? (
                <BookingStepper active={activeStep} onStepClick={stepClickHandler}>
                    {steps.filter(step => !step.hide).map((step) => (
                        <Stepper.Step key={step.step} label={step.metadata.name} allowStepSelect={!step.locked} />
                    ))}
                </BookingStepper>
            ) : (
                <Skeleton height={18} mt={8} width="60%" radius="xl" />
            )}
        />
    );
}